import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, AlertCircle, Clock, CheckCircle, Send, FileText, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function InvoiceStatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
    sent: { label: 'Sent', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    paid: { label: 'Paid', className: 'bg-green-500/20 text-green-300 border-green-500/30' },
    overdue: { label: 'Overdue', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);
  const [clients, setClients] = useState({});

  async function loadInvoices() {
    try {
      const response = await fetch(`${API_URL}/api/invoices`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadClients() {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const clientMap = {};
        // Handle both array response and {clients: [...]} format
        const clientList = Array.isArray(data) ? data : (data.clients || []);
        clientList.forEach(c => {
          clientMap[c.id] = c;
        });
        setClients(clientMap);
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  }

  useEffect(() => {
    loadInvoices();
    loadClients();
  }, []);

  const nowMs = Date.now();

  const overdueCount = useMemo(() => {
    return (items || []).reduce((acc, inv) => {
      const due = inv.due_date ? new Date(inv.due_date).getTime() : null;
      const isServerOverdue = inv.status === 'overdue';
      const isClientOverdue = (inv.status === 'draft' || inv.status === 'sent') && due && due < nowMs;
      return acc + (isServerOverdue || isClientOverdue ? 1 : 0);
    }, 0);
  }, [items, nowMs]);

  const visibleItems = useMemo(() => {
    if (!showOnlyOverdue) return items;
    return (items || []).filter(inv => {
      const due = inv.due_date ? new Date(inv.due_date).getTime() : null;
      const isServerOverdue = inv.status === 'overdue';
      const isClientOverdue = (inv.status === 'draft' || inv.status === 'sent') && due && due < nowMs;
      return isServerOverdue || isClientOverdue;
    });
  }, [items, showOnlyOverdue, nowMs]);

  async function checkOverdues() {
    try {
      setBusy(true);
      const response = await fetch(`${API_URL}/api/invoices/mark-overdue-batch`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data?.detail || 'Failed to check overdues');
      } else {
        toast.success(`Marked ${data.updated || 0} overdue (skipped ${data.skipped || 0})`);
      }
    } catch (error) {
      toast.error('Failed to check overdues');
    } finally {
      setBusy(false);
      loadInvoices();
    }
  }

  const getClientName = (clientId) => {
    return clients[clientId]?.name || 'Unknown Client';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-400" />
            Invoices
          </h1>
          {overdueCount > 0 && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {overdueCount} overdue
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={showOnlyOverdue}
              onChange={(e) => setShowOnlyOverdue(e.target.checked)}
              className="rounded border-slate-600"
            />
            Show only overdue
          </label>
          <Button
            variant="outline"
            onClick={checkOverdues}
            disabled={busy}
            className="border-slate-600 text-white hover:bg-slate-700"
          >
            <Clock className="w-4 h-4 mr-2" />
            {busy ? 'Checking…' : 'Check Overdues'}
          </Button>
          <Button
            onClick={() => navigate('/invoices/new')}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium">Client</th>
                <th className="text-left p-4 text-slate-400 font-medium">Total</th>
                <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                <th className="text-left p-4 text-slate-400 font-medium">Due Date</th>
                <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((inv) => (
                <tr
                  key={inv._id}
                  className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                  onClick={() => navigate(`/invoices/${inv._id}`)}
                >
                  <td className="p-4 text-white">{getClientName(inv.client_id)}</td>
                  <td className="p-4 text-white font-medium">
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      {(inv.amount_total || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="p-4">
                    <InvoiceStatusBadge status={inv.status} />
                  </td>
                  <td className="p-4 text-slate-300">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-indigo-400 hover:text-indigo-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoices/${inv._id}`);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
              {visibleItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No invoices yet</p>
                    <p className="text-sm mt-1">Create your first invoice to get started</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
